import React, { useState, useEffect, useMemo } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import HomeRepairServiceOutlinedIcon from "@mui/icons-material/HomeRepairServiceOutlined";
import * as MuiIcons from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import api from "../../../src/utils/api";
import AddWorkOrderSmart from "./addWorkorder";

const ITEMS_PER_PAGE = 5;

const cardSx = {
  borderRadius: "16px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const nestedCardSx = {
  p: 2,
  borderRadius: "14px",
  border: "1px solid rgba(47,99,173,0.08)",
  backgroundColor: "rgba(255,255,255,0.98)",
  boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
};

const WorkOrders = ({ workOrders = [], propertyId }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [statuses, setStatuses] = useState([]);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [sortOrder, setSortOrder] = useState("newest");

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await api.get(`/statuses`);
        setStatuses(response.data || []);
      } catch (error) {
        console.error("Failed to fetch statuses:", error);
      }
    };
    fetchStatuses();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [sortOrder]);

  const getStatusInfo = (statusName) => {
    return statuses.find((s) => s.statusname === statusName);
  };

  const isResolvedStatus = (order) => {
    const normalized = String(order?.status || "").toLowerCase();
    return (
      !!order?.resolved_at ||
      ["resolved", "closed", "completed"].includes(normalized)
    );
  };

  const resolvedCount = useMemo(() => {
    return workOrders.filter((order) => isResolvedStatus(order)).length;
  }, [workOrders]);

  const openCount = workOrders.length - resolvedCount;

  const sortedWorkOrders = useMemo(() => {
    return [...workOrders].sort((a, b) => {
      const dateA = new Date(a.created_at || a.updated_at || 0);
      const dateB = new Date(b.created_at || b.updated_at || 0);
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
  }, [workOrders, sortOrder]);

  const recentWorkOrders = sortedWorkOrders.slice(0, 2);

  const paginatedWorkOrders = sortedWorkOrders.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setPage(1);
  };

  const renderStatusChip = (order) => {
    const statusInfo = getStatusInfo(order.status);
    const normalized = String(order?.status || "").toLowerCase();

    let chipStyles = {
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.10)",
      fontWeight: 700,
    };

    if (
      ["resolved", "closed", "completed"].includes(normalized) ||
      order?.resolved_at
    ) {
      chipStyles = {
        color: "#15803d",
        backgroundColor: "rgba(34,197,94,0.12)",
        fontWeight: 700,
      };
    } else if (normalized) {
      chipStyles = {
        color: "#b45309",
        backgroundColor: "rgba(245,158,11,0.12)",
        fontWeight: 700,
      };
    }

    return (
      <Chip
        size="small"
        label={statusInfo?.statusname || order.status || "Unknown"}
        sx={chipStyles}
      />
    );
  };

  const renderStatusLine = (order) => {
    const statusInfo = getStatusInfo(order.status);
    const IconComponent = statusInfo?.icon && MuiIcons[statusInfo.icon];
    const themeColor =
      statusInfo?.color && theme.palette[statusInfo.color]
        ? theme.palette[statusInfo.color].main
        : statusInfo?.color || "#5f6b7a";

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {IconComponent && <IconComponent fontSize="small" sx={{ color: themeColor }} />}
        <Typography sx={{ color: themeColor, fontWeight: 700, fontSize: "0.875rem" }}>
          {statusInfo?.statusname || order.status || "Unknown"}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <AddWorkOrderSmart
        open={openAddModal}
        handleClose={() => setOpenAddModal(false)}
        propertyId={propertyId}
        onWorkOrderAdded={() => {
          setShowSuccessSnackbar(true);
          window.location.reload();
        }}
      />

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setShowSuccessSnackbar(false)}
          sx={{ width: "100%" }}
        >
          Work order created successfully!
        </Alert>
      </Snackbar>

      <Box sx={{ width: "100%" }}>
        {/* Header */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#223142",
              mb: 0.5,
            }}
          >
            Work Orders
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
            }}
          >
            Review submitted work orders, status updates, and recent activity.
          </Typography>
        </Box>

        {/* Summary cards + sort */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                      Total Work Orders
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                      {workOrders.length}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "12px",
                      backgroundColor: "rgba(47,99,173,0.10)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <HomeRepairServiceOutlinedIcon sx={{ color: "#2f63ad" }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                      Open Work Orders
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                      {openCount}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "12px",
                      backgroundColor: "rgba(245,158,11,0.12)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <PendingActionsOutlinedIcon sx={{ color: "#d97706" }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ ...cardSx, p: 2.25, minHeight: 128 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.75 }}>
                      Resolved
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142" }}>
                      {resolvedCount}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "12px",
                      backgroundColor: "rgba(34,197,94,0.12)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <CheckCircleOutlineIcon sx={{ color: "#15803d" }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 2.5,
              display: "flex",
              justifyContent: { xs: "stretch", sm: "flex-end" },
            }}
          >
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="sort-label">Sort By</InputLabel>
              <Select
                labelId="sort-label"
                value={sortOrder}
                label="Sort By"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="oldest">Oldest</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Recent section */}
        <Box sx={{ mb: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: "#223142",
              mb: 0.5,
            }}
          >
            Recent Work Orders
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
            }}
          >
            A quick preview of the latest work orders for this account.
          </Typography>
        </Box>

        {workOrders.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
              No work orders found for this account.
            </Typography>

            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setOpenAddModal(true)}
              sx={{
                backgroundColor: "#2f63ad",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#1f4f94",
                  boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                },
              }}
            >
              Create New Work Order
            </Button>
          </Paper>
        ) : (
          <>
            <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
              {recentWorkOrders.map((order) => (
                <Grid item xs={12} md={6} key={order.order_id}>
                  <Paper elevation={0} sx={nestedCardSx}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 1.5,
                        mb: 1.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: "#223142",
                            mb: 0.25,
                          }}
                        >
                          Work Order #{order.order_id}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: "#6b778c",
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          {order.type_name || "N/A"}
                          {order.subtype_name ? ` • ${order.subtype_name}` : ""}
                        </Typography>
                      </Box>

                      {renderStatusChip(order)}
                    </Box>

                    <Box sx={{ mb: 1.5 }}>{renderStatusLine(order)}</Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6b778c",
                        mb: 2,
                        minHeight: 44,
                        flexGrow: 1,
                      }}
                    >
                      {order.description || "No description available."}
                    </Typography>

                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "auto" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityOutlinedIcon />}
                        onClick={() =>
                          navigate(`/home/memberspage/workorder/${order.order_id}`)
                        }
                        sx={{
                          borderRadius: "10px",
                          textTransform: "none",
                          fontWeight: 700,
                        }}
                      >
                        View Details
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Box
              sx={{
                pt: 3,
                pb: 1,
                display: "flex",
                justifyContent: { xs: "stretch", sm: "space-between" },
                gap: 1.5,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setOpenAddModal(true)}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Create New Work Order
              </Button>

              {workOrders.length > 2 && (
                <Button
                  variant="contained"
                  startIcon={<AssignmentTurnedInIcon />}
                  onClick={handleOpen}
                  sx={{
                    backgroundColor: "#2f63ad",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#1f4f94",
                      boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                    },
                  }}
                >
                  View All Work Orders
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Modal */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            boxShadow: "0 18px 60px rgba(0,0,0,0.16)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            color: "#223142",
          }}
        >
          All Work Orders
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            {paginatedWorkOrders.map((order) => (
              <Grid item xs={12} key={order.order_id}>
                <Paper elevation={0} sx={nestedCardSx}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1.5,
                      mb: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: "#223142", mb: 0.25 }}
                      >
                        Work Order #{order.order_id}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#6b778c", fontWeight: 600, mb: 0.5 }}
                      >
                        {order.type_name || "N/A"}
                        {order.subtype_name ? ` • ${order.subtype_name}` : ""}
                      </Typography>
                    </Box>

                    {renderStatusChip(order)}
                  </Box>

                  <Box sx={{ mb: 1.25 }}>{renderStatusLine(order)}</Box>

                  <Stack spacing={0.75}>
                    <Typography variant="body2" sx={{ color: "#223142" }}>
                      <strong>Description:</strong>{" "}
                      {order.description || "No description"}
                    </Typography>
                  </Stack>

                  <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityOutlinedIcon />}
                      onClick={() => {
                        handleClose();
                        navigate(`/home/memberspage/workorder/${order.order_id}`);
                      }}
                      sx={{
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 700,
                      }}
                    >
                      View Details
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            flexDirection: "column",
            gap: 1,
            p: 2,
          }}
        >
          <Pagination
            count={Math.ceil(workOrders.length / ITEMS_PER_PAGE)}
            page={page}
            onChange={(_e, value) => setPage(value)}
            color="primary"
          />
          <Button
            onClick={handleClose}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WorkOrders;
